/**
 * PlanCalibrationEditor — Măsurători pe plan scanat cu calibrare manuală.
 *
 * Flow:
 *  1. User uploads image (jpg/png)
 *  2. User marks 2 points on the plan and enters real distance (e.g. 5.2 m)
 *     -> scale (meters per pixel) is derived
 *  3. User draws polygons on the plan; each vertex adds a point
 *  4. Area (m²) and perimeter (m) computed from pixel coords * scale
 */
import { useRef, useState, useCallback, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Ruler, Plus, Trash2, Save, Download, ArrowLeft, CornerDownRight, Palette, Target, Minus } from "lucide-react";

const PALETTE = ["#0d9488", "#dc2626", "#2563eb", "#f59e0b", "#7c3aed", "#059669"];
const newPolygonName = (i) => (i === 0 ? "Suprafață principală" : `Suprafață extra #${i}`);

// Polygon area (Shoelace)
const polyAreaPx = (pts) => {
  if (pts.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
};
const polyPerimPx = (pts) => {
  if (pts.length < 2) return 0;
  let d = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    if (i === pts.length - 1 && pts.length < 3) break;
    d += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return d;
};

export default function PlanCalibrationEditor({
  companyId, measurement, onBack, onSaved, onExport,
}) {
  const [name, setName] = useState(measurement?.name || "Măsurătoare plan");
  const [address, setAddress] = useState(measurement?.address || "");
  const [imageDataUrl, setImageDataUrl] = useState(measurement?.plan_image_data_url || null);
  const [scale, setScale] = useState(measurement?.plan_scale_meters_per_pixel || null); // meters per pixel

  const [polygons, setPolygons] = useState(() => (
    (measurement?.polygons || []).map((p, i) => ({
      id: p.id || `poly_${Date.now()}_${i}`,
      name: p.name || newPolygonName(i),
      color: p.color || PALETTE[i % PALETTE.length],
      role: p.role === "subtract" ? "subtract" : "add",
      points_px: p.points_px || [],
      area_m2: p.area_m2 || 0,
      perimeter_m: p.perimeter_m || 0,
    }))
  ));
  const [activePolyId, setActivePolyId] = useState(null);

  // Calibration state: 'idle' | 'setting-first' | 'setting-second' | 'entering-distance'
  const [calibMode, setCalibMode] = useState("idle");
  const [calibPoints, setCalibPoints] = useState([]); // [{x,y}, {x,y}]
  const [calibDistanceInput, setCalibDistanceInput] = useState("");
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 500 });
  const [scaleFit, setScaleFit] = useState(1); // display scale (fit inside canvas)

  const wrapRef = useRef(null);

  // Handle image load -> compute display scale
  useEffect(() => {
    if (!imageDataUrl) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImgSize({ w: img.width, h: img.height });
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  // Resize canvas to container
  useEffect(() => {
    if (!wrapRef.current) return;
    const update = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const w = Math.floor(r.width);
      const h = Math.max(420, Math.floor(r.height));
      setCanvasSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (imgSize.w && imgSize.h && canvasSize.w && canvasSize.h) {
      const s = Math.min(canvasSize.w / imgSize.w, canvasSize.h / imgSize.h);
      setScaleFit(s);
    }
  }, [imgSize, canvasSize]);

  // Draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (imgRef.current && imgSize.w) {
      const dispW = imgSize.w * scaleFit;
      const dispH = imgSize.h * scaleFit;
      const ox = (canvas.width - dispW) / 2;
      const oy = (canvas.height - dispH) / 2;
      ctx.drawImage(imgRef.current, ox, oy, dispW, dispH);
      // Polygons
      polygons.forEach((p) => {
        if (!p.points_px.length) return;
        const isSub = p.role === "subtract";
        ctx.save();
        ctx.strokeStyle = isSub ? "#dc2626" : p.color;
        ctx.fillStyle = (isSub ? "#dc2626" : p.color) + "40"; // ~25% alpha
        ctx.lineWidth = isSub ? 3 : 2;
        if (isSub) ctx.setLineDash([6, 4]);
        ctx.beginPath();
        p.points_px.forEach((pt, i) => {
          const x = ox + pt.x * scaleFit;
          const y = oy + pt.y * scaleFit;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        if (p.points_px.length >= 3) ctx.closePath();
        ctx.stroke();
        if (p.points_px.length >= 3) ctx.fill();
        // Vertices
        p.points_px.forEach((pt) => {
          const x = ox + pt.x * scaleFit;
          const y = oy + pt.y * scaleFit;
          ctx.beginPath();
          ctx.arc(x, y, p.id === activePolyId ? 5 : 3, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 1.5;
          ctx.fill();
          ctx.stroke();
        });
        ctx.restore();
      });
      // Calibration overlay
      if (calibPoints.length > 0) {
        ctx.save();
        ctx.strokeStyle = "#f59e0b";
        ctx.fillStyle = "#f59e0b";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        calibPoints.forEach((pt, i) => {
          const x = ox + pt.x * scaleFit;
          const y = oy + pt.y * scaleFit;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          ctx.fillRect(x - 3, y - 3, 6, 6);
        });
        if (calibPoints.length === 2) ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    } else {
      ctx.fillStyle = "#94a3b8";
      ctx.font = "14px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Încarcă o imagine cu planul (JPG/PNG)", canvas.width / 2, canvas.height / 2);
    }
  }, [imgSize, scaleFit, polygons, activePolyId, calibPoints]);

  useEffect(() => { draw(); }, [draw]);

  const canvasToImg = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = ((evt.clientX ?? evt.touches?.[0]?.clientX) - rect.left) * (canvas.width / rect.width);
    const cy = ((evt.clientY ?? evt.touches?.[0]?.clientY) - rect.top) * (canvas.height / rect.height);
    const dispW = imgSize.w * scaleFit;
    const dispH = imgSize.h * scaleFit;
    const ox = (canvas.width - dispW) / 2;
    const oy = (canvas.height - dispH) / 2;
    const x = (cx - ox) / scaleFit;
    const y = (cy - oy) / scaleFit;
    return { x, y };
  };

  const handleCanvasClick = (evt) => {
    if (!imgRef.current) return;
    const p = canvasToImg(evt);
    if (p.x < 0 || p.y < 0 || p.x > imgSize.w || p.y > imgSize.h) return;

    if (calibMode === "setting-first") {
      setCalibPoints([p]);
      setCalibMode("setting-second");
      return;
    }
    if (calibMode === "setting-second") {
      setCalibPoints((prev) => [...prev, p]);
      setCalibMode("entering-distance");
      return;
    }
    if (calibMode === "entering-distance") return;

    // Add point to active polygon (only if scale is set)
    if (!scale) {
      toast.error("Calibrează scara mai întâi (butonul Calibrează).");
      return;
    }
    let aid = activePolyId;
    if (!aid) {
      const np = {
        id: `poly_${Date.now()}`,
        name: newPolygonName(polygons.length),
        color: PALETTE[polygons.length % PALETTE.length],
        role: "add",
        points_px: [p],
        area_m2: 0,
        perimeter_m: 0,
      };
      setPolygons((prev) => [...prev, np]);
      setActivePolyId(np.id);
      return;
    }
    setPolygons((prev) => prev.map((pp) => {
      if (pp.id !== aid) return pp;
      const nextPoints = [...pp.points_px, p];
      const areaPx = polyAreaPx(nextPoints);
      const perimPx = polyPerimPx(nextPoints);
      return {
        ...pp,
        points_px: nextPoints,
        area_m2: areaPx * scale * scale,
        perimeter_m: perimPx * scale,
      };
    }));
  };

  // Recompute metrics when scale changes
  useEffect(() => {
    if (!scale) return;
    setPolygons((prev) => prev.map((pp) => {
      const areaPx = polyAreaPx(pp.points_px);
      const perimPx = polyPerimPx(pp.points_px);
      return { ...pp, area_m2: areaPx * scale * scale, perimeter_m: perimPx * scale };
    }));
  }, [scale]);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(reader.result);
      setScale(null);
      setPolygons([]);
      setActivePolyId(null);
      setCalibPoints([]);
      setCalibMode("idle");
    };
    reader.readAsDataURL(file);
  };

  const startCalibration = () => {
    if (!imgRef.current) { toast.error("Încarcă întâi o imagine"); return; }
    setCalibMode("setting-first");
    setCalibPoints([]);
    setCalibDistanceInput("");
  };

  const applyCalibration = () => {
    const d = parseFloat(calibDistanceInput.replace(",", "."));
    if (!isFinite(d) || d <= 0) { toast.error("Introdu o distanță validă în metri."); return; }
    if (calibPoints.length !== 2) { toast.error("Setează 2 puncte pe imagine."); return; }
    const dx = calibPoints[1].x - calibPoints[0].x;
    const dy = calibPoints[1].y - calibPoints[0].y;
    const px = Math.hypot(dx, dy);
    if (px < 1) { toast.error("Punctele sunt prea apropiate."); return; }
    const s = d / px; // meters per pixel
    setScale(s);
    setCalibMode("idle");
    setCalibPoints([]);
    toast.success(`Calibrat: 1 px = ${s.toFixed(5)} m (segment ${d.toFixed(2)} m).`);
  };

  const handleAddSurface = (role = "add") => {
    if (!scale) { toast.error("Calibrează scara mai întâi."); return; }
    const p = {
      id: `poly_${Date.now()}`,
      name: role === "subtract"
        ? `Suprafață scăzută #${polygons.filter(x => x.role === "subtract").length + 1}`
        : newPolygonName(polygons.filter(x => x.role !== "subtract").length),
      color: PALETTE[polygons.length % PALETTE.length],
      role,
      points_px: [],
      area_m2: 0,
      perimeter_m: 0,
    };
    setPolygons((prev) => [...prev, p]);
    setActivePolyId(p.id);
    toast.info(role === "subtract" ? "Click pe plan pentru zona ce se scade." : "Click pe plan pentru puncte.");
  };

  const handleUndoLast = () => {
    if (!activePolyId) return;
    setPolygons((prev) => prev.map((pp) => {
      if (pp.id !== activePolyId) return pp;
      const nextPoints = pp.points_px.slice(0, -1);
      return {
        ...pp, points_px: nextPoints,
        area_m2: polyAreaPx(nextPoints) * (scale || 0) * (scale || 0),
        perimeter_m: polyPerimPx(nextPoints) * (scale || 0),
      };
    }));
  };

  const handleDeletePolygon = (id) => {
    setPolygons((prev) => prev.filter((p) => p.id !== id));
    if (activePolyId === id) setActivePolyId(null);
  };

  const totalArea = Math.max(0, polygons.reduce((s, p) => (
    s + (p.role === "subtract" ? -1 : 1) * (p.area_m2 || 0)
  ), 0));
  const totalPerim = polygons.filter(p => p.role !== "subtract")
    .reduce((s, p) => s + (p.perimeter_m || 0), 0);

  const buildPayload = () => ({
    name,
    mode: "plan",
    address,
    plan_image_data_url: imageDataUrl,
    plan_scale_meters_per_pixel: scale,
    polygons: polygons.map((p) => ({
      id: p.id, name: p.name, color: p.color, role: p.role || "add",
      points_px: p.points_px, area_m2: p.area_m2, perimeter_m: p.perimeter_m,
    })),
    total_area_m2: totalArea,
    total_perimeter_m: totalPerim,
  });

  const saveMeasurement = async () => {
    if (!polygons.some((p) => p.points_px.length >= 3)) {
      toast.error("Trebuie cel puțin o suprafață cu 3 puncte.");
      return null;
    }
    try {
      setSaving(true);
      const axios = (await import("axios")).default;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const params = { company_id: companyId };
      const payload = buildPayload();
      let saved;
      if (measurement?.id) {
        const { data } = await axios.patch(`${API}/sketches/measurements/${measurement.id}`, payload, { params });
        saved = data.measurement;
      } else {
        const { data } = await axios.post(`${API}/sketches/measurements`, payload, { params });
        saved = data.measurement;
      }
      toast.success("Măsurătoare salvată");
      return saved;
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut salva măsurătoarea");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const saved = await saveMeasurement();
    if (saved && onSaved) onSaved(saved);
  };
  const handleExport = async () => {
    const saved = await saveMeasurement();
    if (saved && onExport) onExport(saved);
  };

  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="plan-measure-editor">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="plan-measure-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Înapoi
        </Button>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Denumire" className="max-w-xs h-9" />
        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Adresă (opțional)" className="max-w-sm h-9" />
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm">
          <Upload className="w-4 h-4" />
          <span>{imageDataUrl ? "Schimbă planul" : "Încarcă plan"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="plan-upload-input" />
        </label>
        <Button size="sm" variant="outline" onClick={startCalibration} disabled={!imageDataUrl}>
          <Target className="w-4 h-4 mr-1" /> Calibrează scara
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-slate-700 hover:bg-slate-800 text-white">
            <Save className="w-4 h-4 mr-1" /> Salvează
          </Button>
          <Button size="sm" onClick={handleExport} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Download className="w-4 h-4 mr-1" /> Exportă în DevizePRO
          </Button>
        </div>
      </div>

      {/* Info bar */}
      <div className="px-4 py-2 border-b border-slate-200 bg-white flex flex-wrap items-center gap-3 text-sm">
        <Badge className={`${scale ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"} border-0`}>
          {scale ? `Calibrat: 1px = ${(scale * 1000).toFixed(2)} mm` : "Necalibrat"}
        </Badge>
        <span className="text-slate-600"><b>Arie totală:</b> <span className="text-teal-700 font-semibold">{totalArea.toFixed(2)} m²</span></span>
        <span className="text-slate-600"><b>Perimetru:</b> <span className="font-semibold">{totalPerim.toFixed(2)} m</span></span>

        {calibMode === "setting-first" && (
          <span className="text-amber-700 font-medium">Click primul punct de calibrare pe plan…</span>
        )}
        {calibMode === "setting-second" && (
          <span className="text-amber-700 font-medium">Click al doilea punct de calibrare…</span>
        )}
        {calibMode === "entering-distance" && (
          <div className="flex items-center gap-2 ml-auto">
            <Ruler className="w-4 h-4 text-amber-600" />
            <span>Distanța reală:</span>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={calibDistanceInput}
              onChange={(e) => setCalibDistanceInput(e.target.value)}
              placeholder="m"
              className="w-24 h-8"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") applyCalibration(); }}
              data-testid="calib-distance-input"
            />
            <Button size="sm" onClick={applyCalibration} className="h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="calib-apply-btn">
              Aplică
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setCalibMode("idle"); setCalibPoints([]); }}>Anulează</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px]">
        <div ref={wrapRef} className="bg-slate-900 relative" style={{ height: "68vh", minHeight: 480 }}>
          <canvas
            ref={canvasRef}
            width={canvasSize.w}
            height={canvasSize.h}
            onClick={handleCanvasClick}
            className="w-full h-full block cursor-crosshair"
            style={{ touchAction: "none" }}
            data-testid="plan-canvas"
          />
        </div>

        <div className="border-l border-slate-200 bg-slate-50 flex flex-col" style={{ maxHeight: "68vh" }}>
          <div className="p-3 border-b border-slate-200 bg-white flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => handleAddSurface("add")}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              disabled={!scale}
              data-testid="plan-add-surface-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Suprafață
            </Button>
            <Button
              size="sm"
              onClick={() => handleAddSurface("subtract")}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!scale}
              data-testid="plan-add-hole-btn"
            >
              <Minus className="w-4 h-4 mr-1" /> Scăzută
            </Button>
            <Button size="sm" variant="outline" onClick={handleUndoLast} disabled={!activePolyId}>
              <CornerDownRight className="w-4 h-4 mr-1" /> Undo
            </Button>
          </div>
          <div className="p-3 text-xs text-slate-600 border-b border-slate-200 bg-white">
            {scale
              ? "Click pe plan = adaugă punct la suprafața activă."
              : "Încarcă un plan și calibrează scara pentru a începe."}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {polygons.map((p) => (
              <div
                key={p.id}
                onClick={() => setActivePolyId(p.id)}
                className={`rounded-lg border bg-white p-2.5 cursor-pointer transition ${
                  p.id === activePolyId ? "ring-2 ring-teal-500 border-teal-300" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: p.role === "subtract" ? "#dc2626" : p.color }} />
                  <Input
                    value={p.name}
                    onChange={(e) => setPolygons((prev) => prev.map((pp) => (pp.id === p.id ? { ...pp, name: e.target.value } : pp)))}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 text-sm px-2 border-slate-200"
                  />
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); handleDeletePolygon(p.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs">
                  {p.role === "subtract" && (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Scădere</Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]">
                    Arie: <b className={`ml-1 ${p.role === "subtract" ? "text-red-700" : "text-teal-700"}`}>
                      {p.role === "subtract" ? "−" : ""}{p.area_m2.toFixed(2)} m²
                    </b>
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">Perim: <b className="ml-1">{p.perimeter_m.toFixed(2)} m</b></Badge>
                  <Badge variant="outline" className="text-[10px]">Puncte: {p.points_px.length}</Badge>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolygons(prev => prev.map(pp => pp.id === p.id ? { ...pp, role: pp.role === "subtract" ? "add" : "subtract" } : pp));
                    }}
                    className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-100"
                  >
                    {p.role === "subtract" ? "→ Adaugă" : "→ Scade"}
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-1.5">
                  <Palette className="w-3 h-3 text-slate-400" />
                  {PALETTE.map((c) => (
                    <button key={c} type="button"
                      onClick={(e) => { e.stopPropagation(); setPolygons((prev) => prev.map((pp) => (pp.id === p.id ? { ...pp, color: c } : pp))); }}
                      className={`w-4 h-4 rounded-sm border ${p.color === c ? "border-slate-900 ring-1 ring-slate-900" : "border-white shadow-sm"}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
